import { getFirebaseConfig } from "./config";

function getGtmSessionIdFixed(measurementId: string) {
  const name = '_ga_' + measurementId;
  const match = document.cookie.match(new RegExp(name + '=GS\\d\\.\\d\\.(s?(\\d+))'));

  if (match && match[2]) {
    // match[2] 会精准匹配到 s 之后的纯数字部分
    return match[2];
  }
  return null;
}

/**
 * GA4 session id —— 从 `_ga_<MEASUREMENT_ID>` cookie 里取（形如 `_ga_XXXX=GS2.1.s1712345678$o3$…`）。
 *
 * **为什么不能只认一个 id**（2026-09-10 实测）：
 * 1. 本函数原先读 `window.GA_ID`，而这个全局变量**全仓没有任何地方赋值**
 *    （`apps/ packages/ scripts/ public/` 全量 grep 仅此一处读取）→ cookie 名被拼成
 *    `_ga_undefined` → sessionId 恒为空串，`usePayment` / `useAppPurchaseFlow` 发给后端的
 *    `ga-session-id` 头一直是空的，后端用 Measurement Protocol 补 `purchase` 时挂不进会话。
 * 2. 生产站的 GA4 是由 **GTM 容器**下发的（`GTM-5TQNBQGS` → `G-D517P0FSVX` / `G-0RHCTHJEEF`），
 *    与本仓 `config.ts` 里的 prod `measurementId`（`G-1LJC5P2LD7`，Firebase web 应用那条流）
 *    **不是同一个**。谁在页面上真正跑，session id 就只存在于谁的 cookie 里。
 *
 * 所以：先按配置里的 measurementId 精确取，取不到再扫任意 `_ga_*` cookie 兜底。
 */
function readGa4SessionId(): string {
  const configured = (() => {
    try {
      return getFirebaseConfig()?.measurementId?.replace(/^G-/, "");
    } catch {
      // NEXT_PUBLIC_ENV 缺失/非法时 getFirebaseConfig 会抛，这里只做兜底不影响主流程
      return undefined;
    }
  })();
  if (configured) {
    const exact = getGtmSessionIdFixed(configured);
    if (exact) {
      return exact;
    }
  }
  // 兜底：页面上实际在跑的那条 GA4 流（可能来自 GTM 容器，id 与本仓配置不同）
  return document.cookie.match(/_ga_[A-Z0-9]+=GS\d\.\d\.s?(\d+)/)?.[1] || "";
}

// 获取GA instance Id 和 Session Id；web没有instanceId返回clientId
export const getGaInfo = () => {
  if (typeof window === "undefined") {
    return { gaInstanceId: undefined, sessionId: undefined, clientId: "" };
  }
  const appDeviceInfo = (window as any).__APP_DEVICE_INFO__;
  if (appDeviceInfo) {
    return {
      gaInstanceId: appDeviceInfo.gaInstanceId,
      sessionId: appDeviceInfo.sessionId,
    };
  }
  const clientId = document.cookie.match(/_ga=GA1\.\d+\.(\d+\.\d+)/)?.[1] || "";
  const sessionId = readGa4SessionId();

  return {
    clientId: clientId,
    sessionId: sessionId,
  };
};

export type GaInfo = ReturnType<typeof getGaInfo>;

/**
 * 上报用 GA 信息：优先使用 getGaInfo()；
 * 若宿主通过 window.__requestGAInfoFromNative 注入了“向原生请求”的实现，
 * 且在 App 内缺 gaInstanceId/sessionId 时会调用并合并结果（用于弥补注入失败或延迟）。
 */
export async function getGaInfoForReporting(): Promise<GaInfo> {
  const gaInfo = getGaInfo();
  if (typeof window === "undefined") {
    return gaInfo;
  }
  const requestFromNative = (window as any).__requestGAInfoFromNative as
    | (() => Promise<{ gaInstanceId?: string; sessionId?: string }>)
    | undefined;
  const needFallback =
    typeof requestFromNative === "function" &&
    (!gaInfo.gaInstanceId || !gaInfo.sessionId);
  if (needFallback) {
    try {
      const res = await requestFromNative!();
      if (res?.gaInstanceId) {
        gaInfo.gaInstanceId = res.gaInstanceId;
      }
      if (res?.sessionId) {
        gaInfo.sessionId = res.sessionId;
      }
    } catch {
      // 超时或旧壳未实现，使用当前 getGaInfo() 结果
    }
  }
  return gaInfo;
}